import {isFeatureEnabled} from '@github-ui/feature-flags'
import {sendEvent} from '@github-ui/hydro-analytics'
import {ERRORS} from '@github-ui/item-picker-base/constants/errors'
import {IssueTypePicker, type IssueTypePickerIssueType} from '@github-ui/item-picker-issue-type-relay'
import {commitUpdateIssueIssueTypeMutation} from '@github-ui/item-picker-issue-type-relay/commitUpdateIssueIssueTypeMutation'
// eslint-disable-next-line no-restricted-imports
import {useToastContext} from '@github-ui/toast/ToastContext'
import {useAnalytics} from '@github-ui/use-analytics'
import type {SxProp} from '@primer/styled-react'
import {useCallback, useMemo, useState} from 'react'
import {graphql, useFragment, useRelayEnvironment} from 'react-relay'

import {LABELS} from '../../constants/labels'
import {userRejectedCopilotTypeSuggestion} from '../../utils/copilot-utils'
import {ReadonlySectionHeader} from '../ReadonlySectionHeader'
import {Section} from '../Section'
import {SectionHeader} from '../SectionHeader'
import type {TypesSectionFragment$key} from './__generated__/TypesSectionFragment.graphql'
import type {TypesSectionTypeFragment$key} from './__generated__/TypesSectionTypeFragment.graphql'
import {CopilotTypesSection} from './CopilotTypesSection'
import {IssueType} from './IssueType'
import styles from './TypesSection.module.css'

const ReadonlyTypesSectionHeader = () => <ReadonlySectionHeader title={LABELS.sectionTitles.types} />

type TypesSectionProps = {
  sectionHeader: React.ReactElement
  type: IssueTypePickerIssueType | null
  repoNameWithOwner?: string
} & SxProp

const TypesSection = ({sectionHeader, type, repoNameWithOwner, sx}: TypesSectionProps) => (
  <Section
    id={`sidebar-types-section`}
    sx={sx}
    sectionHeader={sectionHeader}
    emptyText={type ? undefined : LABELS.emptySections.types}
  >
    <div className={styles.typeContainer}>
      <IssueType type={type} repoNameWithOwner={repoNameWithOwner} />
    </div>
  </Section>
)

export type CreateIssueIssueTypesSectionProps = {
  repo: string
  owner: string
  type: IssueTypePickerIssueType | null
  onSelectionChange: (type: IssueTypePickerIssueType | null) => void
  viewerCanType: boolean
  insidePortal: boolean
} & SxProp
export function CreateIssueIssueTypesSection({
  type,
  onSelectionChange,
  viewerCanType,
  sx,
  ...sharedConfigProps
}: CreateIssueIssueTypesSectionProps) {
  const {sendAnalyticsEvent} = useAnalytics()
  const sectionHeader = () => {
    if (!viewerCanType) {
      return <ReadonlyTypesSectionHeader />
    } else {
      return (
        <IssueTypePicker
          onSelectionChange={newType => {
            const issueType = newType?.[0] || null
            sendAnalyticsEvent('issue_create.update_issue_type', 'ISSUE_CREATE_UPDATE_ISSUE_TYPE_DIALOG', {
              issueTypeId: issueType?.id ?? '',
            })
            onSelectionChange(issueType)
          }}
          readonly={viewerCanType}
          activeIssueType={type}
          anchorElement={(anchorProps, ref) => (
            <SectionHeader title={LABELS.sectionTitles.types} buttonProps={anchorProps} ref={ref} />
          )}
          {...sharedConfigProps}
          width="medium"
        />
      )
    }
  }

  const {repo, owner} = sharedConfigProps

  return <TypesSection sx={sx} sectionHeader={sectionHeader()} type={type} repoNameWithOwner={`${owner}/${repo}`} />
}

// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
export const TypesSectionTypeFragment = graphql`
  fragment TypesSectionTypeFragment on Issue {
    issueType {
      id
      name
      isEnabled
      description
      color
      pinnedFields {
        __typename
        ... on IssueFieldText {
          id
          name
          dataType
        }
        ... on IssueFieldSingleSelect {
          id
          name
          dataType
        }
        ... on IssueFieldDate {
          id
          name
          dataType
        }
        ... on IssueFieldNumber {
          id
          name
          dataType
        }
      }
    }
  }
`

// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
export const TypesSectionFragment = graphql`
  fragment TypesSectionFragment on Issue {
    repository {
      name
      nameWithOwner
      owner {
        login
      }
      issueTypes(first: 100) {
        edges {
          node {
            id
          }
        }
      }
    }
    id
    ...TypesSectionTypeFragment
    viewerCanType
  }
`

type EditIssueIssueTypesSectionProps = {
  issue: TypesSectionFragment$key
  onIssueUpdate?: () => void
  insideSidePanel?: boolean
  readonly: boolean
}
export function EditIssueIssueTypeSection({
  issue,
  onIssueUpdate,
  insideSidePanel,
  readonly,
}: EditIssueIssueTypesSectionProps) {
  const data = useFragment(TypesSectionFragment, issue)
  const {
    repository: {
      owner: {login: owner},
      name: repo,
      nameWithOwner,
      issueTypes,
    },
    id: issueId,
    viewerCanType,
  } = data

  const formattedIssueTypes = issueTypes?.edges?.map(edge => edge?.node?.id) || []

  const {issueType} = useFragment<TypesSectionTypeFragment$key>(TypesSectionTypeFragment, data)

  const activeType = issueType || null

  const {sendAnalyticsEvent} = useAnalytics()
  const {addToast} = useToastContext()
  const environment = useRelayEnvironment()

  const copilot_auto_assign_metadata = isFeatureEnabled('copilot_auto_assign_metadata')

  // only show the copilot component if
  // 1. feature flag is enabled,
  // 2. the user can change the issue type, AND
  // 3. there is no type assigned to issue
  const [shouldShowCopilotComponent, setShouldShowCopilotComponent] = useState(
    copilot_auto_assign_metadata &&
      viewerCanType &&
      (activeType === null || activeType === undefined) &&
      !userRejectedCopilotTypeSuggestion(issueId),
  )

  const onSelectionChanged = useCallback(
    (selectedTypes: IssueTypePickerIssueType[]) => {
      if (shouldShowCopilotComponent) {
        setShouldShowCopilotComponent(false)
        sendEvent('copilot_suggested_type.skipped', {issueId})
      }

      const issueTypeId = selectedTypes.length > 0 ? selectedTypes[0]?.id : null
      sendAnalyticsEvent('issue_viewer.update_issue_type', 'ISSUE_VIEWER_UPDATE_ISSUE_TYPE_DIALOG', {
        initialIssueTypeId: activeType?.id ?? '',
        issueTypeId: issueTypeId ?? '',
      })

      commitUpdateIssueIssueTypeMutation({
        environment,
        input: {issueId, issueTypeId},
        onError: () =>
          // eslint-disable-next-line @github-ui/dotcom-primer/toast-migration
          addToast({
            type: 'error',
            message: ERRORS.couldNotUpdateType,
          }),
        onCompleted: onIssueUpdate,
      })
    },
    [activeType?.id, addToast, environment, issueId, onIssueUpdate, sendAnalyticsEvent, shouldShowCopilotComponent],
  )

  const sectionHeader = useMemo(() => {
    const readonlyType = readonly || !viewerCanType
    if (readonlyType) {
      return <ReadonlyTypesSectionHeader />
    }

    return (
      <IssueTypePicker
        repo={repo}
        owner={owner}
        onSelectionChange={onSelectionChanged}
        anchorElement={(anchorProps, ref) => (
          <SectionHeader title={LABELS.sectionTitles.types} buttonProps={anchorProps} ref={ref} />
        )}
        readonly={readonlyType}
        activeIssueType={activeType ?? null}
        insidePortal={insideSidePanel}
        width="medium"
      />
    )
  }, [readonly, viewerCanType, repo, owner, onSelectionChanged, activeType, insideSidePanel])

  if (!viewerCanType && formattedIssueTypes.length === 0) return null

  if (!shouldShowCopilotComponent) {
    return <TypesSection sectionHeader={sectionHeader} type={activeType ?? null} repoNameWithOwner={nameWithOwner} />
  } else {
    return (
      <CopilotTypesSection
        issueId={issueId}
        onIssueUpdate={onIssueUpdate}
        sectionHeader={sectionHeader}
        setShouldShowCopilotComponent={setShouldShowCopilotComponent}
      />
    )
  }
}

try{ ReadonlyTypesSectionHeader.displayName ||= 'ReadonlyTypesSectionHeader' } catch {}
try{ TypesSection.displayName ||= 'TypesSection' } catch {}
try{ CreateIssueIssueTypesSection.displayName ||= 'CreateIssueIssueTypesSection' } catch {}
try{ EditIssueIssueTypeSection.displayName ||= 'EditIssueIssueTypeSection' } catch {}